import type { GrayImage } from "./types";

/**
 * Build a single-channel "pip-ness" map from RGBA pixels, where higher values
 * mean a pixel is more likely to be part of a pip rather than the white tile.
 *
 * Dominoes in colour-coded sets use a different pip colour per number (black,
 * orange, blue, red, grey, …). A plain luminance threshold fails because a
 * bright orange pip is barely darker than the white tile, and because one very
 * dark pip colour on a tile drags a global threshold away from the lighter
 * colours. Measuring how far each pixel is from "white" instead catches every
 * colour:
 *
 *   score = max(darkness, chroma)
 *     darkness = 255 - luma        → catches black / grey / any dark pip
 *     chroma   = max(r,g,b) - min(r,g,b) → catches saturated orange/blue/red pips
 *
 * The white tile has high luma and near-zero chroma, so it scores low; every
 * pip colour scores high.
 */
export function toPipScore(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): GrayImage {
  const expected = width * height * 4;
  if (rgba.length !== expected) {
    throw new Error(
      `RGBA buffer length ${rgba.length} does not match ${width}x${height} (expected ${expected})`,
    );
  }

  const data = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < data.length; i++, p += 4) {
    const r = rgba[p];
    const g = rgba[p + 1];
    const b = rgba[p + 2];
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const darkness = 255 - luma;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    data[i] = Math.round(Math.max(darkness, chroma));
  }

  return { width, height, data };
}

/**
 * Build a pip-score map from a grayscale image alone (no colour information).
 * Equivalent to the darkness term of {@link toPipScore}; used for grayscale
 * inputs and synthetic black-on-white images.
 */
export function darknessScore(gray: GrayImage): GrayImage {
  const data = new Uint8ClampedArray(gray.data.length);
  for (let i = 0; i < data.length; i++) {
    data[i] = 255 - gray.data[i];
  }
  return { width: gray.width, height: gray.height, data };
}
